import { useEffect, useState } from "react";
import { getServiceRoles } from "../apis/serviceRoleMappingApi";
import servicesConfig from "../config/ServicesConfig";
import { useAuth } from "../Context/AuthContext";

// Backend role ids are snake_case (fpo_admin); normalizedRoles keys are camelCase (fpoAdmin).
// Fixed, mechanical conversion — see RBAC_PERMISSIONS_AUDIT.md §6.2 ("three parallel, unmapped
// role-naming conventions") and PERMISSIONS_IMPLEMENTATION_PLAN.md's Backend-Driven Route
// Membership section for why this is a documented detail, not a design problem.
const toCamelRoleKey = (id = "") => id.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

// Module-level cache, one fetch per accessKey per browser session — same pattern as
// hooks/useVisibleServices.js's _cachedConfigs/_fetchPromise for getServiceConfigs().
const _cache = new Map();

function fetchRoleKeysForService(accessKey) {
  if (!_cache.has(accessKey)) {
    _cache.set(
      accessKey,
      getServiceRoles(accessKey)
        .then((res) => (res.data?.data || []).map((mapping) => toCamelRoleKey(mapping.role?.id)))
        .catch(() => {
          _cache.delete(accessKey); // allow retry on next mount rather than caching a failure
          return [];
        })
    );
  }
  return _cache.get(accessKey);
}

/**
 * Pilot for Backend-Driven Route Membership (see PERMISSIONS_IMPLEMENTATION_PLAN.md).
 * Resolves whether the current user's normalizedRoles intersect with the roles the backend
 * has mapped to this route's service (ServiceRoleMappingEntity), instead of a hardcoded
 * allowedRoles array. serviceKey is the frontend key (e.g. "myeducation"); the backend
 * accessKey (e.g. "MY_EDUCATION") is resolved via the same servicesConfig.accessKey field
 * ServicesLayout.jsx already uses for accessibility rules — no new translation table.
 */
export function useServiceRoleMembership(serviceKey) {
  const { normalizedRoles } = useAuth();
  const accessKey = servicesConfig[serviceKey]?.accessKey || null;
  const [state, setState] = useState({ loading: !!accessKey, isMember: false });

  useEffect(() => {
    if (normalizedRoles?.systemAdmin) {
      setState({ loading: false, isMember: true });
      return;
    }
    if (!accessKey) {
      // No backend service concept for this route — fail closed, matches allowedRoles.length
      // > 0 semantics elsewhere in ProtectedRoutes (an unconfigured route denies, not allows).
      setState({ loading: false, isMember: false });
      return;
    }
    let alive = true;
    setState((prev) => ({ ...prev, loading: true }));
    fetchRoleKeysForService(accessKey).then((roleKeys) => {
      if (!alive) return;
      setState({ loading: false, isMember: roleKeys.some((key) => normalizedRoles?.[key]) });
    });
    return () => {
      alive = false;
    };
  }, [accessKey, normalizedRoles]);

  return state;
}
